import { useOffline } from '@/contexts/OfflineContext';
import { Wifi, WifiOff, RefreshCw, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';

export function OfflineIndicator() {
  const { isOnline, syncStatus, queueLength } = useOffline();
  const [displayState, setDisplayState] = useState<'hidden' | 'offline' | 'syncing' | 'online'>('hidden');
  const [wasOffline, setWasOffline] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const isSyncing = syncStatus === 'syncing';
  const justSynced = syncStatus === 'success';

  // Track when we go offline or come back online
  useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
      setDisplayState('offline');
      if (timerRef.current) clearTimeout(timerRef.current);
    } else if (wasOffline && displayState === 'offline') {
      // We just came back online - if no sync needed, show 'online' briefly then hide
      if (queueLength === 0) {
        setDisplayState('online');
        timerRef.current = setTimeout(() => {
          setDisplayState('hidden');
          setWasOffline(false);
        }, 1500);
      }
      // If there's a queue, the syncing effect will handle the transition
    }
  }, [isOnline, wasOffline, displayState, queueLength]);

  // Handle syncing state
  useEffect(() => {
    if (isSyncing && wasOffline) {
      setDisplayState('syncing');
      if (timerRef.current) clearTimeout(timerRef.current);
    }
  }, [isSyncing, wasOffline]);

  // Handle sync complete - show syncing for 1s, then online for 1s, then hide
  useEffect(() => {
    if (justSynced && wasOffline && displayState === 'syncing') {
      // Keep showing "syncing" for 1 second
      timerRef.current = setTimeout(() => {
        setDisplayState('online');
        // Show "online" for 1 second, then hide
        timerRef.current = setTimeout(() => {
          setDisplayState('hidden');
          setWasOffline(false);
        }, 1000);
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [justSynced, wasOffline, displayState]);

  const shouldShow = displayState !== 'hidden';

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          initial={{ y: -24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -24, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className={`fixed top-0 left-0 right-0 z-40 h-6 flex items-center justify-center gap-1.5 text-[11px] font-medium ${
            displayState === 'offline'
              ? 'bg-red-500 text-white'
              : displayState === 'syncing'
              ? 'bg-orange-500 text-white'
              : 'bg-emerald-500 text-white'
          }`}
          data-testid="connection-banner"
        >
          {displayState === 'offline' ? (
            <>
              <WifiOff className="h-3 w-3" />
              <span data-testid="text-offline-status">Offline</span>
            </>
          ) : displayState === 'syncing' ? (
            <>
              <RefreshCw className="h-3 w-3 animate-spin" />
              <span data-testid="text-syncing-status">Syncing...</span>
            </>
          ) : (
            <>
              <Check className="h-3 w-3" />
              <span data-testid="text-sync-success">Online</span>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
